import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import ExhibitorConsent from '@components/app/ExhibitorConsent';
import useMeQuery from '@hooks/useMeQuery';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import useAppFeatures from '@hooks/useAppFeatures';
import { useAuthExpired } from '@/apollo/authExpired';
import { useCancelMeasurePageLoad } from '@cvent/sli-nextjs-metrics';
import { useTranslate } from 'nucleus-text';
import AppTitle from '@components/app/AppTitle';
import { AttendeePreferenceType } from '@graphql/gql/graphql';
import { ONBOARDING_ATTENDEE_PREFERENCE_VALUE } from '@components/onboarding/constants';
import ExpiredAuth from './ExpiredAuth';
import Terms from './Terms';
import SwitchLoginEventModal from './SwitchLoginEventModal';
import useLoginBroadcast from './useLoginBroadcast';
import useReloadOnAttendeeChange from './useReloadOnAttendeeChange';

const cancelMeasurePageLoadReason = 'Measure page_load cancelled due to PageTakeover un-mounting child components';
function PageTakeover({ children }) {
  const { translate } = useTranslate();
  const {
    data: {
      me: {
        id: attendeeId,
        consent,
        exhibitorConsentQuestion,
        exhibitorConsentAnswer,
        guest,
        preferences,
        event: { id: eventId, title: eventName } = {}
      } = {}
    } = {}
  } = useMeQuery();

  const { authExpired, setAuthExpired } = useAuthExpired();

  const [showLoginSwitcher, setShowLoginSwitcher] = useState(false);
  const loggedInEvent = useLoginBroadcast({ eventId, eventName });

  useEffect(() => {
    if (loggedInEvent?.id && eventId) {
      const conflict = loggedInEvent?.id !== eventId;
      setShowLoginSwitcher(conflict);
      if (conflict) {
        setAuthExpired(true);
      }
    }
  }, [loggedInEvent.id, eventId, setAuthExpired]);

  useReloadOnAttendeeChange(attendeeId, eventId);

  const cancelMeasurePageLoad = useCancelMeasurePageLoad();

  // Attendee terms
  const [showTerms, setShowTerms] = useState(null);
  const onAcceptTerms = () => {
    setShowTerms(false);
  };
  const showAttendeeTerms = showTerms == null ? consent?.id != null && !consent?.hasAccepted : showTerms;

  // Exhibitor consent
  const [showConsent, setShowConsent] = useState(null);
  const onAnswerExhibitorConsentQuestion = () => {
    setShowConsent(false);
  };
  const showExhibitorConsent =
    showConsent == null ? exhibitorConsentQuestion?.id != null && exhibitorConsentAnswer?.choice == null : showConsent;

  const router = useRouter();
  const { profileOnboarding } = useAppFeatures();
  const { isFeatureSettingEnabled } = useAttendeeHubConfig();
  const onboardingEnabled = isFeatureSettingEnabled('ATTENDEE_ONBOARDING');
  const [onboardingShown, setOnboardingShown] = useState(false);

  if (showLoginSwitcher) {
    cancelMeasurePageLoad(cancelMeasurePageLoadReason);
    return (
      <>
        <AppTitle title={eventName} />
        <SwitchLoginEventModal
          loggedInEvent={loggedInEvent}
          initialEvent={{
            id: eventId,
            name: eventName
          }}
        />
      </>
    );
  }

  if (authExpired) {
    cancelMeasurePageLoad(cancelMeasurePageLoadReason);
    return (
      <>
        <AppTitle title={translate('Login-Expired-Error-Page-Title')} />
        <ExpiredAuth />
      </>
    );
  }

  if (showAttendeeTerms) {
    cancelMeasurePageLoad(cancelMeasurePageLoadReason);
    return (
      <>
        <AppTitle title={eventName} />
        <Terms {...consent} onAccept={onAcceptTerms} />
      </>
    );
  }

  if (!showAttendeeTerms && showExhibitorConsent) {
    cancelMeasurePageLoad(cancelMeasurePageLoadReason);
    return (
      <>
        <AppTitle title={eventName} />
        <ExhibitorConsent
          {...exhibitorConsentQuestion}
          attendeeId={attendeeId}
          onAnswer={onAnswerExhibitorConsentQuestion}
        />
      </>
    );
  }

  if (
    profileOnboarding &&
    onboardingEnabled &&
    !onboardingShown &&
    eventId &&
    !guest &&
    preferences &&
    !preferences?.some(
      pref =>
        pref.preferenceType === AttendeePreferenceType.OnboardingProgress &&
        pref.preferenceValue === ONBOARDING_ATTENDEE_PREFERENCE_VALUE
    )
  ) {
    const currentPath = router.asPath;
    // don't want to redirect if we're already on an onboarding route
    if (!currentPath.includes('onboarding')) {
      setOnboardingShown(true);
      router.push(`/events/${eventId}/onboarding/welcome?originalRoute=${currentPath}`);
    }
  }

  return children;
}

export default PageTakeover;
